'use client';
import { prepareCaptchaBody } from '@eliadmin/core/src/api/recaptcha-client';
import { CaptchaAction } from '@eliadmin/core/src/api/recaptcha-actions';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import {
  type FormValidationMessages,
  useFormValidationMessages,
} from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import {
  fetchCountries,
  fetchLanguages,
  fetchRegions,
} from '@eliadmin/core/src/api/general/general-api';
import { fetchJobCategories } from '@eliadmin/core/src/api/jobportal/categories-api';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Field } from '@eliadmin/core/src/components/form/field';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { Button } from '@eliadmin/core/src/components/ui/button';

type Option = { value: string | number; label: string };
const EMPTY_ARRAY: never[] = [];
const CATEGORY_QUERY = { childs: false, page: 1, results: 100 } as const;

function readCandidateApiError(payload: unknown) {
  const data =
    payload && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : {};
  const fieldPayload =
    data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)
      ? data.errors
      : data;
  const fields: Record<string, string[]> = {};

  for (const [field, value] of Object.entries(fieldPayload)) {
    if (!Array.isArray(value)) continue;
    const messages = value.filter((message): message is string => typeof message === 'string');
    if (messages.length) fields[field] = messages;
  }

  const message = [data.message, data.error].find(
    (value): value is string => typeof value === 'string' && !!value.trim(),
  );

  return { fields, message: message || Object.values(fields).flat().join('\n') };
}

export const registerCandidateSchema = (VM: FormValidationMessages) =>
  z.object({
    // Profesia
    categories: z
      .array(z.object({ value: z.union([z.string(), z.number()]), label: z.string() }))
      .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    default_languages: z
      .array(z.object({ value: z.union([z.string(), z.number()]), label: z.string() }))
      .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    is_self_employed: z.boolean().default(false),

    // Osobné údaje
    title: z.string().optional(),
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z.string().min(12, VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE()),

    // Adresa
    address: z.string().optional(),
    street_number: z.string().optional(),
    city: z.string().optional(),
    postcode: z.string().optional(),
    country_id: z.string().optional(),
    region_id: z.string().optional(),

    // Prístup
    // password: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
    // password_confirmation: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),

    // Súhlas
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    newsletter: z.boolean().optional(),
    newsletter_return_url: z.string().default(''),

    'g-recaptcha-response': z.string().default(''),
  });
// .refine(data => data.password === data.password_confirmation, {
//   message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
//   path: ['password_confirmation'],
// });

type FormValues = z.infer<ReturnType<typeof registerCandidateSchema>>;

export function CandidateRegisterForm({ locale }: { locale: string }) {
  const { t } = useTranslations();
  const { toast } = useToast();
  const router = useRouter();

  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: languagesData } = useQuery({
    queryKey: [CacheKey.LANGUAGES, locale],
    queryFn: () => fetchLanguages({ query: { page: 1, results: 100 } }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: categoriesData } = useQuery({
    queryKey: [CacheKey.JOBPORTAL_CATEGORIES, locale],
    queryFn: () => fetchJobCategories({ locale, query: CATEGORY_QUERY }),
    staleTime: 5 * 60 * 1000,
  });

  const { data: countriesData } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    staleTime: 5 * 60 * 1000,
  });

  const languageOptions = useMemo<Option[]>(
    () => languagesData?.data.map(l => ({ value: l.id, label: l.name })) ?? EMPTY_ARRAY,
    [languagesData],
  );

  const countryOptions = useMemo<Option[]>(
    () => countriesData?.data.map(c => ({ value: String(c.id), label: c.name })) ?? EMPTY_ARRAY,
    [countriesData],
  );

  const categoryOptions = useMemo<Option[]>(
    () => categoriesData?.data.map(c => ({ value: c.id, label: c.name })) ?? EMPTY_ARRAY,
    [categoriesData],
  );

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<FormValues>({
    resolver: zodResolver(registerCandidateSchema(formValidationMessages)),
    defaultValues: {
      categories: [],
      default_languages: [],
      is_self_employed: false,
      title: '',
      name: '',
      surname: '',
      email: '',
      phone: '',
      address: '',
      street_number: '',
      city: '',
      postcode: '',
      country_id: '',
      region_id: '',
      //    password: '',
      //    password_confirmation: '',
      gdpr: false,
      newsletter: false,
      newsletter_return_url: process.env.NEXT_PUBLIC_APP_URL ?? '',
      'g-recaptcha-response': '',
    },
  });

  const countryId = form.watch('country_id');

  const { data: regionsData } = useQuery({
    queryKey: [CacheKey.REGIONS, locale, countryId],
    queryFn: () => fetchRegions({ locale, query: { country_id: [countryId!], results: 1000 } }),
    enabled: !!countryId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    form.setValue('region_id', '');
  }, [countryId, form]);

  const regionOptions = useMemo<Option[]>(
    () => regionsData?.data.map(r => ({ value: String(r.id), label: r.name })) ?? EMPTY_ARRAY,
    [regionsData],
  );

  const handleSubmit = form.handleSubmit(async data => {
    setIsSubmitting(true);

    const uploadedFiles: { id: number }[] = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch(process.env.NEXT_PUBLIC_API_URL + '/store/files', {
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ',
          },
          method: 'POST',
          body: formData,
        });

        if (res.status === 201) {
          const json = await res.json();
          uploadedFiles.push({ id: json.data.id });
        } else {
          toast({ title: 'Nastala chyba pri nahrávaní súborov', variant: 'destructive' });
          setIsSubmitting(false);
          return;
        }
      } catch {
        toast({ title: 'Nastala chyba pri nahrávaní súborov', variant: 'destructive' });
        setIsSubmitting(false);
        return;
      }
    }

    // 2. Get reCaptcha token

    // 3. Build POST body
    const body: Record<string, unknown> = {
      name: data.name,
      surname: data.surname,
      email: data.email,
      title: data.title || undefined,
      phone: data.phone.replace('+', ''),
      categories: data.categories.map(item => ({ id: item.value })),
      default_languages: data.default_languages.map(item => ({ id: item.value })),
      is_self_employed: data.is_self_employed,
      address: data.address || undefined,
      street_number: data.street_number || undefined,
      city: data.city || undefined,
      postcode: data.postcode || undefined,
      ...(data.country_id ? { country: { id: data.country_id } } : {}),
      ...(data.region_id ? { region: { id: parseInt(data.region_id, 10) } } : {}),
      ...(uploadedFiles.length > 0 ? { attachments: uploadedFiles } : {}),
      gdpr: data.gdpr,
      newsletter: data.newsletter ?? false,
      newsletter_return_url: data.newsletter_return_url,

    };

    try {
      const res = await fetch(process.env.NEXT_PUBLIC_API_URL + '/job-portal/candidate', {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Accept-Language': 'sk',
          Authorization: 'Bearer ',
        },
        method: 'POST',
        body: JSON.stringify(await prepareCaptchaBody(body, CaptchaAction.CANDIDATE)),
      });

      if (res.status === 200 || res.status === 201) {
        toast({ title: t('text3120'), variant: 'success' });
        router.push('/');
      } else if (res.status === 422) {
        const { fields, message } = readCandidateApiError(await res.json().catch(() => null));
        // Map API validation errors back to form fields
        for (const [field, messages] of Object.entries(fields)) {
          form.setError(field as Parameters<typeof form.setError>[0], {
            type: 'manual',
            message: messages.join('\n'),
          });
        }
        toast({
          title: 'Skontrolujte zadané údaje',
          description: message || undefined,
          variant: 'destructive',
        });
      } else {
        const { message } = readCandidateApiError(await res.json().catch(() => null));
        toast({ title: message || 'Nastala chyba', variant: 'destructive' });
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        for (const [field, messages] of Object.entries(error.fields)) form.setError(field as Parameters<typeof form.setError>[0], { type: 'server', message: messages.join('\n') });
      }
      toast({ title: t('text1472'), variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Form {...form}>
      <CaptchaFormMessage errors={form.formState.errors} />
      <form onSubmit={handleSubmit} className={cn('w-full')}>
        <div className="grid w-full grid-cols-12 gap-x-4 gap-y-6 sm:gap-y-8">
          {/* ── Profesia ── */}
          <FormTitle title={t('text896')} />

          <Field
            control={form.control}
            name="categories"
            label="Profesia"
            placeholder="Vyberte profesiu"
            options={categoryOptions}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            type="multiselect"
            required
          />

          <Field
            control={form.control}
            name="default_languages"
            label="Jazyky"
            placeholder={t('text901')}
            options={languageOptions}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            type="multiselect"
            required
          />

          <Field
            control={form.control}
            name="is_self_employed"
            label="Samostatne zárobkovo činná osoba (SZČO)"
            className={{ wrapper: 'col-span-12' }}
            type="checkbox"
          />

          {/* ── Osobné údaje ── */}
          <FormTitle title={t('text198')} className="mt-4" />

          <Field
            control={form.control}
            name="title"
            label="Titul"
            placeholder="Ing., Mgr., Bc., ..."
            className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
          />

          <Field
            control={form.control}
            name="name"
            label="Meno"
            placeholder={t('text2240')}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
            required
          />

          <Field
            control={form.control}
            name="surname"
            label="Priezvisko"
            placeholder={t('text2241')}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
            required
          />

          <Field
            control={form.control}
            name="email"
            label="E-mail"
            placeholder="Zadajte e-mail"
            type="email"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            required
          />

          <Field
            control={form.control}
            name="phone"
            label={t('text36')}
            placeholder="+421 900 000 000"
            type="tel"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
            required
          />

          {/* ── Adresa ── */}
          <FormTitle title="Adresa" className="mt-4" />

          <Field
            control={form.control}
            name="address"
            label="Ulica"
            placeholder="Zadajte ulicu"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-8' }}
          />

          <Field
            control={form.control}
            name="street_number"
            label={t('text485')}
            placeholder="Zadajte číslo domu"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-4' }}
          />

          <Field
            control={form.control}
            name="postcode"
            label={t('text40')}
            placeholder={t('text781')}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-3' }}
          />

          <Field
            control={form.control}
            name="city"
            label="Mesto"
            placeholder="Zadajte mesto"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-9' }}
          />

          <Field
            control={form.control}
            name="country_id"
            label="Krajina"
            placeholder={t('text904')}
            options={countryOptions}
            type="select"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />

          <Field
            control={form.control}
            name="region_id"
            label="Kraj"
            placeholder={t('text905')}
            options={regionOptions}
            type="select"
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />

          {/* ── Prílohy ── */}
          <div className="col-span-12 mt-4 flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-foreground">{t('text898')}</p>
              <p className="text-sm text-muted-foreground">
                {t('text899')}
              </p>
              {files.length > 0 && (
                <p className="text-sm text-muted-foreground">Počet príloh: {files.length}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0 rounded-lg border border-border bg-input px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
            >
              {t('text907')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files ?? [])])}
            />
          </div>

          {/* ── Súhlas ── */}
          <FormTitle title="Súhlas" className="mt-4" />

          <Field
            control={form.control}
            name="gdpr"
            label="Súhlasím so spracovaním osobných údajov (GDPR)"
            className={{ wrapper: 'col-span-12' }}
            type="checkbox"
            required
          />

          <Field
            control={form.control}
            name="newsletter"
            label="Chcem dostávať novinky a pracovné ponuky e-mailom"
            className={{ wrapper: 'col-span-12' }}
            type="checkbox"
          />

          {/* ── Submit ── */}
          <div className="col-span-12 mt-4 flex justify-end">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => console.log(form.formState.errors)}
            >
              {isSubmitting ? 'Odosielam...' : t('text1534')}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

function FormTitle({ title, className }: { title: string; className?: string }) {
  return (
    <h4 className={cn('col-span-12 w-full text-xl font-medium sm:text-2xl', className)}>{title}</h4>
  );
}
